/** @import { BufferManagerWebGPU } from "../BufferManagerWebGPU" */
/** @import { MemoryBlock } from "../../memory/MemoryBlock" */

export const UINTS_PER_DRAWCALL = 5;
export const UINTS_PER_EDGE_DRAWCALL = 2;

export const BYTES_PER_DRAWCALL = 4 * UINTS_PER_DRAWCALL;
export const BYTES_PER_EDGE_DRAWCALL = 4 * UINTS_PER_EDGE_DRAWCALL;

// This alignment is required to specify the GPUBufferBinding.offset property,
// used for the bindings in the culling compute shader.
export const INDIRECT_DRAW_ALIGNMENT = 256;

// In an IndirectBuffer with indexed drawCalls, each element contains a sequence of 5 subsequent uint32 values.
// This const provides for each parameter the corresponding uint32-offset where to find it within a single drawCall entry.
const DrawCallOffsets = {
  indexCount: 0,
  instanceCount: 1,
  firstIndex: 2,
  baseVertex: 3,
  firstInstance: 4
};

/**
 * Records drawCalls into an indirectBuffer on GPU that can be used for MultiDrawIndirect calls.
 *
 * How to use:
 * // 1. Create builder with WebGPU device
 * const builder = new IndirectBufferBuilder(device);
 *
 * // 2. Add draw calls. (may be instanced). See function comments for details.
 * builder.addDrawCall(indexCount, instanceCount, firstIndex, baseVertex, firstInstance, indexCountEdges, firstIndexEdges)
 * builder.addDrawCall(...)
 * ...
 *
 * // 3. Finally, upload commands to GPU:
 * // Allocate and upload draw call data to an indirect BufferManagerWebGPU.
 * const gpuBuffer = builder.uploadDrawCalls(bufferManager); // for main drawCalls
 * const gpuBufferEdges = builder.uploadEdgeDrawCalls(bufferManager); // for edge drawCalls
 *
 * // 4. The returned GPUBuffer can be used as indirectBuffer parameter in a GPURenderPassEncoder:
 * renderPass.drawIndexedIndirect(gpuBuffer, ...);
 *
 * // or (if supported)
 * renderPass.multiDrawIndexedIndirect(gpuBuffer, ..);
 */
export class IndirectBufferBuilder {

  /** @type {GPUDevice} */
  #device;
  #nextIndex = 0;
  #data = null; // Uint32Array with serialized indexed draw calls (indexCount, instanceCount, firstIndex, baseVertex, firstInstance)

  // Uint32Array with pairs (indexCount, firstIndex) used for edge drawCalls.
  // For edges, most drawCall values are identical with the main drawCalls.
  // Therefore, we only collect the values that need to be replaced for the corresponding edge rendering call.
  // For each main drawCall in #data, there is an (indexCount, firstIndex) pair in #dataEdges. The edges drawCall is
  // defined by the main drawCall after replacing instanceCount and firstIndex with the values from #dataEdges.
  #dataEdges = null;

  #instanceCountTotal = 0;

  /**
   * @param {GPUDevice} device
   * @param {number} maxItems
   */
  constructor(device, maxItems) {
    this.#data = new Uint32Array(maxItems * UINTS_PER_DRAWCALL);
    this.#dataEdges = new Uint32Array(maxItems * UINTS_PER_EDGE_DRAWCALL);
    this.#nextIndex = 0; // index where to add next draw call
    this.#device = device;
  }

  // Start filling again
  reset() {
    this.#nextIndex = 0;
    this.#instanceCountTotal = 0;
  }

  // The multiDrawCalls of a RenderBatch define a sequence of instances to be rendered.
  // Return value is an array that maps each instance index to the drawCall it belongs to.
  // This mapping is needed to run the culling shader.
  createInstanceToDrawCalls() {
    const drawCallCount = this.#nextIndex;

    // Alloc arrays to be filled
    const instanceToDrawCall = new Uint32Array(this.#instanceCountTotal);

    let rangeStart = 0;
    for (let i = 0; i < drawCallCount; i++) {

      // Read instanceCount of next drawCall
      const instanceCount = this.#data[i * UINTS_PER_DRAWCALL + DrawCallOffsets.instanceCount];

      // Fill range with drawCallIndex i
      const rangeEnd = rangeStart + instanceCount;
      instanceToDrawCall.fill(i, rangeStart, rangeEnd);

      // Track summed instance count of drawCalls processed so far
      rangeStart = rangeEnd;
    }

    // Create GPU buffers
    const instanceToDrawCallBuffer = this.#device.createBuffer({
      label: 'instance-to-draw-call-buffer',
      size: instanceToDrawCall.byteLength,
      usage: GPUBufferUsage.COPY_DST | GPUBufferUsage.STORAGE
    });

    // Copy data to GPU buffers
    this.#device.queue.writeBuffer(instanceToDrawCallBuffer, 0, instanceToDrawCall.buffer);
    return instanceToDrawCallBuffer;
  }

  /**
   * Uploads draw call data to it's own storage buffer, to be used as input in
   * a compute shader.
   * @returns {GPUBuffer}
   */
  uploadDrawCallsAsBuffer() {
    const byteLength = this.#nextIndex * BYTES_PER_DRAWCALL;

    // Create new buffer if needed
    const gpuBuffer = this.#device.createBuffer({
      label: 'indirect-draw-buffer',
      // might be smaller than the CPU-side buffer, because we now know the final count
      size: byteLength,
      usage: GPUBufferUsage.COPY_DST | GPUBufferUsage.STORAGE
    });

    this.#device.queue.writeBuffer(gpuBuffer, 0, this.#data.buffer, 0, byteLength);
    return gpuBuffer;
  }

  /**
   * Allocates a block in indirect bufferManager and uploads draw call data.
   * @param {BufferManagerWebGPU} bufferManager
   * @returns {MemoryBlock}
   */
  uploadDrawCalls(bufferManager) {
    const byteLength = this.#nextIndex * BYTES_PER_DRAWCALL;

    let memoryBlock = bufferManager.allocate(byteLength, INDIRECT_DRAW_ALIGNMENT);
    this.#device.queue.writeBuffer(
      memoryBlock.buffer, memoryBlock.offset, this.#data.buffer, 0, byteLength);

    return memoryBlock;
  }

  /**
   * Upload edge data to to GPU. Note that the result is not a valid indirectBuffer, i.e., cannot be used directly in a render pass.
   * The buffer only contains the 2-uints per drawCall (firstIndex and indexCount) that need to be replaced in the main drawCalls when rendering edges.
   * @returns {GPUBuffer}
   **/
  uploadEdgeData() {
    const byteLength = this.#nextIndex * BYTES_PER_EDGE_DRAWCALL;

    // Create new buffer if needed
    const gpuBuffer = this.#device.createBuffer({
      label: 'indirect-edge-buffer',
      // might be smaller than the CPU-side buffer, because we now know the final count
      size: byteLength,
      usage: GPUBufferUsage.COPY_DST | GPUBufferUsage.STORAGE
    });

    this.#device.queue.writeBuffer(gpuBuffer, 0, this.#dataEdges.buffer, 0, byteLength);
    return gpuBuffer;
  }

  /**
   * Allocates a block in indirect bufferManager and uploads self-contained
   * edge drawCalls. It can be used directly in a render pass, but is actually
   * not needed when using the culling shader.
   * @param {BufferManagerWebGPU} bufferManager
   * @returns {MemoryBlock}
   */
  uploadEdgeDrawCalls(bufferManager) {
    // Use main drawCalls and replace firstIndex and instanceCount to turn it into edge drawCalls.
    // Note: When using the culling shader, this happens automatically in the compute shader.
    const dataCpu = this.#data.slice();
    for (let i = 0; i < this.#nextIndex; i++) {
      const srcOffset = i * UINTS_PER_EDGE_DRAWCALL;
      const dstOffset = i * UINTS_PER_DRAWCALL;
      dataCpu[dstOffset + DrawCallOffsets.indexCount] = this.#dataEdges[srcOffset];
      dataCpu[dstOffset + DrawCallOffsets.firstIndex] = this.#dataEdges[srcOffset + 1];
    }

    const byteLength = this.#nextIndex * BYTES_PER_DRAWCALL;

    let memoryBlock = bufferManager.allocate(byteLength, INDIRECT_DRAW_ALIGNMENT);
    this.#device.queue.writeBuffer(
      memoryBlock.buffer, memoryBlock.offset, dataCpu.buffer, 0, byteLength);

    return memoryBlock;
  }

  addDrawCall(indexCount, instanceCount, firstIndex, baseVertex, firstInstance, indexCountEdges, firstIndexEdges) {
    let offset = this.#nextIndex * UINTS_PER_DRAWCALL;
    this.#data[offset + DrawCallOffsets.indexCount] = indexCount;
    this.#data[offset + DrawCallOffsets.instanceCount] = instanceCount;
    this.#data[offset + DrawCallOffsets.firstIndex] = firstIndex;
    this.#data[offset + DrawCallOffsets.baseVertex] = baseVertex;
    this.#data[offset + DrawCallOffsets.firstInstance] = firstInstance;

    const edgeOffset = this.#nextIndex * UINTS_PER_EDGE_DRAWCALL;
    this.#dataEdges[edgeOffset] = indexCountEdges;
    this.#dataEdges[edgeOffset + 1] = firstIndexEdges;

    // Track total instance count
    this.#instanceCountTotal += instanceCount;

    this.#nextIndex++;
  }

  nextIndex() {
    return this.#nextIndex;
  }

  empty() {
    return this.#instanceCountTotal == 0;
  }

  // Summed instance count of all drawCalls processed so far
  getInstanceCount() {
    return this.#instanceCountTotal;
  }

  getDrawCallCount() {
    return this.#nextIndex;
  }
}